import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE as BASE_FROM_CLIENT, API_PREFIX, authFetch, url } from './apiClient';
import { monthLabel, rupees } from './dashboardStats';
import { formatDate } from './dateFormat';
import Icon from './Icons';
import './Dashboard.css';

const API_BASE = `${BASE_FROM_CLIENT}${API_PREFIX}/tenants`;

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const num = (v) => Number(v) || 0;
const billAmount = (b) => num(b.rent) + num(b.water) + num(b.electricity) + num(b.miscellaneous);
const byRoom = (a, b) => String(a.tenantName).localeCompare(String(b.tenantName), undefined, { numeric: true, sensitivity: 'base' });
const sum = (list, f) => list.reduce((t, b) => t + f(b), 0);
const via = (v) => (v === 'ONLINE' ? 'Online' : v === 'OWNER' ? 'Cash / UPI' : '—');

function Room({ bill, names }) {
  const name = names[bill.tenantName];
  return (
    <div className="who">
      <span className="who__avatar">{String(bill.tenantName).replace(/\D/g, '') || '?'}</span>
      <span>
        <span className="who__name">{name || bill.tenantName}</span>
        {name && <span className="who__room">{bill.tenantName}</span>}
      </span>
    </div>
  );
}

function Via({ value }) {
  return <span className={`chip chip--${value === 'ONLINE' ? 'green' : 'brown'}`}>{via(value)}</span>;
}

export default function AdminPaidBillsReport() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(thisMonth());
  const [bills, setBills] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auth guard: only ADMIN
  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) return navigate('/login', { replace: true });
    const { role } = JSON.parse(stored);
    if (role !== 'ADMIN') navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    authFetch(url.adminUsersAll())
      .then((r) => (r.ok ? r.json() : []))
      .then((users) => setNames(Object.fromEntries((users || []).filter((u) => u.fullName).map((u) => [u.username, u.fullName]))))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    if (!month) return;
    setLoading(true); setError('');
    try {
      const res = await authFetch(`${API_BASE}/paid-bills/${month}`);
      if (!res.ok) throw new Error('Fetch failed');
      const raw = await res.json();
      setBills(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.error(e);
      setError('Unable to load report');
      setBills([]);
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rent = bills.filter((b) => b.billType !== 'ELECTRICITY').sort(byRoom);
  const elec = bills.filter((b) => b.billType === 'ELECTRICITY').sort(byRoom);
  const rentTotal = sum(rent, billAmount);
  const elecTotal = sum(elec, (b) => num(b.electricity));

  const exportCsv = () => {
    if (!bills.length) return alert('No data to export');
    const header = ['Type', 'Room', 'Name', 'Month', 'Rent', 'Water', 'Electricity', 'Misc', 'Total', 'Paid on', 'Via'];
    const rows = [...rent, ...elec].map((b) => [
      b.billType === 'ELECTRICITY' ? 'Electricity' : 'Rent', b.tenantName, names[b.tenantName] || '', b.monthYear,
      num(b.rent), num(b.water), num(b.electricity), num(b.miscellaneous), billAmount(b), formatDate(b.paidDate), via(b.paidVia),
    ]);
    const footer = ['', '', '', 'Totals', sum(bills, (b) => num(b.rent)), sum(bills, (b) => num(b.water)),
      sum(bills, (b) => num(b.electricity)), sum(bills, (b) => num(b.miscellaneous)), rentTotal + elecTotal, '', ''];
    const cell = (v) => (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : `${v}`);
    const csv = [header, ...rows, footer].map((r) => r.map(cell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `VGR-paid-bills-${month}.csv`;
    a.click();
    URL.revokeObjectURL(href);
  };

  // Real PDF file (not the print dialog) with the VGR logo; rent and electricity in separate tables.
  const exportPdf = async () => {
    if (!bills.length) return alert('No data to export');
    try {
      const [{ jsPDF }, { default: autoTable }, { buildPaidReportPdf }] = await Promise.all([
        import('jspdf'), import('jspdf-autotable'), import('./paidReportPdf'),
      ]);
      const logo = await fetch('/vgr-logo.png').then((r) => r.blob()).then((blob) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      })).catch(() => null);
      buildPaidReportPdf({ jsPDF, autoTable, bills, month, names, logo }).save(`VGR-paid-bills-${month}.pdf`);
    } catch (e) {
      console.error(e);
      alert('Could not make the PDF');
    }
  };

  return (
    <div className="dash">
      <div className="dash-head">
        <div>
          <h2 className="dash-title">Paid Report</h2>
          <p className="dash-sub">Bills paid for <strong>{monthLabel(month)}</strong></p>
        </div>
        <div className="dash-actions">
          <label className="field-inline">
            <span>Month</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
          <button type="button" className="btn btn--ghost" onClick={fetchReport} disabled={!month || loading}>
            <Icon name="refresh" size={16} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={exportCsv} disabled={!bills.length}>Export CSV</button>
          <button type="button" className="btn btn--primary" onClick={exportPdf} disabled={!bills.length}>
            <Icon name="receipt" size={16} /> Download PDF
          </button>
        </div>
      </div>

      {error && <div className="alert alert--danger">{error}</div>}

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi__icon kpi__icon--brown"><Icon name="home" size={22} /></div>
          <div className="kpi__value">{rupees(rentTotal)}</div>
          <div className="kpi__label">Rent collected</div>
          <div className="kpi__sub">{rent.length} bill{rent.length === 1 ? '' : 's'} paid</div>
        </div>
        <div className="kpi">
          <div className="kpi__icon kpi__icon--amber"><Icon name="bolt" size={22} /></div>
          <div className="kpi__value">{rupees(elecTotal)}</div>
          <div className="kpi__label">Electricity collected</div>
          <div className="kpi__sub">{elec.length} bill{elec.length === 1 ? '' : 's'} paid</div>
        </div>
        <div className="kpi">
          <div className="kpi__icon kpi__icon--green"><Icon name="check" size={22} /></div>
          <div className="kpi__value">{rupees(rentTotal + elecTotal)}</div>
          <div className="kpi__label">Total collected</div>
          <div className="kpi__sub">{bills.length} bills paid</div>
        </div>
      </div>

      {!loading && !error && bills.length === 0 ? (
        <div className="panel empty">
          <Icon name="receipt" size={36} />
          <h3>No paid bills for {monthLabel(month)}</h3>
          <p>Pick another month, or mark bills as paid first.</p>
        </div>
      ) : (
        <>
          <div className="panel">
            <div className="panel__head">
              <h3><span className="chip chip--brown"><Icon name="home" size={14} /> Rent</span> <span className="count">{rent.length}</span></h3>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Room</th><th className="num">Rent</th><th className="num">Water</th><th className="num">Misc</th>
                    <th className="num">Total</th><th>Paid on</th><th>Via</th>
                  </tr>
                </thead>
                <tbody>
                  {rent.length === 0 && <tr><td colSpan={7} className="muted">No rent bills paid yet.</td></tr>}
                  {rent.map((b) => (
                    <tr key={b.id}>
                      <td><Room bill={b} names={names} /></td>
                      <td className="num">{rupees(b.rent)}</td>
                      <td className="num">{rupees(b.water)}</td>
                      <td className="num">{rupees(b.miscellaneous)}</td>
                      <td className="num"><strong>{rupees(billAmount(b))}</strong></td>
                      <td>{formatDate(b.paidDate) || '—'}</td>
                      <td><Via value={b.paidVia} /></td>
                    </tr>
                  ))}
                  {rent.length > 0 && (
                    <tr style={{ background: 'var(--primary-tint)' }}>
                      <td><strong>Total</strong></td>
                      <td className="num"><strong>{rupees(sum(rent, (b) => num(b.rent)))}</strong></td>
                      <td className="num"><strong>{rupees(sum(rent, (b) => num(b.water)))}</strong></td>
                      <td className="num"><strong>{rupees(sum(rent, (b) => num(b.miscellaneous)))}</strong></td>
                      <td className="num"><strong>{rupees(rentTotal)}</strong></td>
                      <td /><td />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <h3><span className="chip chip--amber"><Icon name="bolt" size={14} /> Electricity</span> <span className="count">{elec.length}</span></h3>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Room</th><th className="num">Amount</th><th>Paid on</th><th>Via</th></tr>
                </thead>
                <tbody>
                  {elec.length === 0 && <tr><td colSpan={4} className="muted">No electricity bills paid yet.</td></tr>}
                  {elec.map((b) => (
                    <tr key={b.id}>
                      <td><Room bill={b} names={names} /></td>
                      <td className="num"><strong>{rupees(b.electricity)}</strong></td>
                      <td>{formatDate(b.paidDate) || '—'}</td>
                      <td><Via value={b.paidVia} /></td>
                    </tr>
                  ))}
                  {elec.length > 0 && (
                    <tr style={{ background: 'var(--warning-tint)' }}>
                      <td><strong>Total</strong></td>
                      <td className="num"><strong>{rupees(elecTotal)}</strong></td>
                      <td /><td />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
